import { useEffect } from "react";
import useSWR from "swr";
import type { ProductVariantDto } from "../../../api";
import { likesService } from "../../api_client";
import type { PaginatedResponse } from "../../pagination/data";
import {
  WISHLIST_PAGES_KEY_PREFIX,
  useWishlistStore,
} from "../domain/stores/useWishlistStore";

type LikedVariantDto = ProductVariantDto & { liked?: boolean | null };

export interface WishlistVariantsQuery {
  page?: number;
  pageSize?: number;
  /**
   * Reserved — backend currently ignores this. Kept on the type so callers
   * don't need to change when search lands server-side.
   */
  search?: string;
}

/**
 * Fetches the current user's liked variants via the typed `LikesApi`.
 * Falls back to the Zustand cache on error so the wishlist page still
 * renders something useful offline.
 */
export function useWishlistVariants(query?: WishlistVariantsQuery) {
  const page = Number(query?.page ?? 1);
  const pageSize = Number(query?.pageSize ?? 10);

  const cacheKey = `${WISHLIST_PAGES_KEY_PREFIX}${page}-${pageSize}`;

  const swr = useSWR<PaginatedResponse<LikedVariantDto>>(
    cacheKey,
    async () => {
      const res = await likesService.getMyLikes(page, pageSize);
      return res.data as unknown as PaginatedResponse<LikedVariantDto>;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  // Keep the client store in sync with backend-reported `liked` flags.
  const syncLikedStates = useWishlistStore((s) => s.syncLikedStates);
  useEffect(() => {
    if (swr.data?.data) syncLikedStates(swr.data.data);
  }, [swr.data, syncLikedStates]);

  // When a variant is unliked, optimistically drop it from the in-memory list
  // so the grid updates without waiting on a refetch.
  const likedIds = useWishlistStore((s) => s.wishlistVariantIds);
  const filtered = swr.data
    ? {
        ...swr.data,
        data: swr.data.data.filter((v) => likedIds.includes(v.id)),
      }
    : swr.data;

  return {
    data: filtered,
    isLoading: swr.isLoading ?? (!swr.data && !swr.error),
    error: swr.error,
    refresh: swr.mutate,
  };
}
